<template>
  <div v-if="visible" class="menu-preview-modal" @click.self="onClose">
    <div class="menu-preview-modal__dialog">
      <header class="menu-preview-modal__header">
        <div class="menu-preview-modal__heading">
          <h4>{{ title }}</h4>
          <p v-if="subtitle" class="menu-preview-modal__subtitle">{{ subtitle }}</p>
        </div>
        <div class="menu-preview-modal__actions">
          <button
            type="button"
            class="btn add"
            :disabled="busy || importing || !hasCategories"
            @click="$emit('import')"
          >
            {{ importing
              ? $t('gestiune.integrationsMenuPreviewImporting')
              : $t('gestiune.integrationsMenuPreviewImport') }}
          </button>
          <button
            type="button"
            class="btn delete menu-preview-modal__close-btn"
            :disabled="busy || importing"
            @click="onClose"
          >
            {{ $t('general.close') }}
          </button>
        </div>
      </header>

      <div
        v-if="!busy && !errorMessage && hasCategories"
        class="menu-preview-modal__toolbar"
      >
        <input
          v-model="searchQuery"
          type="search"
          class="menu-preview-modal__search"
          :placeholder="$t('gestiune.integrationsMenuPreviewSearchPlaceholder')"
          :disabled="importing"
          autocomplete="off"
        >
        <button
          v-if="searchQuery"
          type="button"
          class="menu-preview-modal__search-clear"
          :disabled="importing"
          @click="searchQuery = ''"
        >
          {{ $t('gestiune.integrationsMenuPreviewSearchClear') }}
        </button>
      </div>

      <div v-if="warnings.length" class="alert alert-warning" role="status"><p v-for="(warning, index) in warnings" :key="index">{{ warningMessage(warning) }}</p></div>
      <div class="menu-preview-modal__content">
        <div v-if="busy" class="menu-preview-modal__loading">
          <div class="menu-preview-modal__spinner" aria-hidden="true">
            <svg viewBox="25 25 50 50" class="menu-preview-modal__spinner-svg">
              <circle cx="50" cy="50" r="20" fill="none" class="menu-preview-modal__spinner-path"></circle>
            </svg>
          </div>
          <p>{{ $t('gestiune.integrationsMenuPreviewLoading') }}</p>
        </div>

        <div v-else-if="errorMessage" class="menu-preview-modal__error">
          {{ errorMessage }}
        </div>

        <div v-else-if="!filteredCategories.length" class="menu-preview-modal__error">
          {{ searchQuery
            ? $t('gestiune.integrationsMenuPreviewSearchEmpty')
            : $t('gestiune.integrationsGlovoMenuEmpty') }}
        </div>

        <div v-else class="menu-preview-modal__body">
          <wolt-menu-layout
            v-if="layout === 'wolt'"
            :categories="filteredCategories"
            :currency="currency"
          />
          <glovo-menu-layout
            v-else-if="layout === 'glovo'"
            :categories="filteredCategories"
            :currency="currency"
          />
          <bolt-menu-layout
            v-else
            :categories="filteredCategories"
            :currency="currency"
          />
        </div>

        <div v-if="importing" class="menu-preview-modal__importing">
          <div class="menu-preview-modal__spinner" aria-hidden="true">
            <svg viewBox="25 25 50 50" class="menu-preview-modal__spinner-svg">
              <circle cx="50" cy="50" r="20" fill="none" class="menu-preview-modal__spinner-path"></circle>
            </svg>
          </div>
          <p>{{ $t('gestiune.integrationsMenuPreviewImporting') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import WoltMenuLayout from './WoltMenuLayout.vue'
import GlovoMenuLayout from './GlovoMenuLayout.vue'
import BoltMenuLayout from './BoltMenuLayout.vue'

export default {
  name: 'IntegrationMenuPreviewModal',
  components: {
    WoltMenuLayout,
    GlovoMenuLayout,
    BoltMenuLayout
  },
  props: {
    visible: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
    importing: { type: Boolean, default: false },
    layout: { type: String, default: 'wolt' },
    currency: { type: String, default: 'RON' },
    categories: { type: Array, default: () => [] },
    errorMessage: { type: String, default: '' },
    warnings: { type: Array, default: () => [] },
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' }
  },
  data () {
    return {
      searchQuery: ''
    }
  },
  computed: {
    hasCategories () {
      return Array.isArray(this.categories) && this.categories.length > 0
    },
    normalizedQuery () {
      return String(this.searchQuery || '').trim().toLowerCase()
    },
    filteredCategories () {
      const cats = Array.isArray(this.categories) ? this.categories : []
      const q = this.normalizedQuery
      if (!q) return cats

      const out = []
      cats.forEach((category) => {
        const catName = String((category && category.name) || '').toLowerCase()
        const catId = String((category && category.id) || '').toLowerCase()
        const products = Array.isArray(category.products) ? category.products : []
        const matchedProducts = products.filter((product) => this.productMatches(product, q))

        if (matchedProducts.length) {
          out.push(Object.assign({}, category, { products: matchedProducts }))
        } else if (catName.indexOf(q) !== -1 || catId.indexOf(q) !== -1) {
          out.push(category)
        }
      })
      return out
    }
  },
  watch: {
    visible (val) {
      if (typeof document === 'undefined') return
      document.body.style.overflow = val ? 'hidden' : ''
      if (!val) this.searchQuery = ''
    },
    categories () {
      this.searchQuery = ''
    }
  },
  beforeDestroy () {
    if (typeof document !== 'undefined') {
      document.body.style.overflow = ''
    }
  },
  methods: {
    warningMessage (warning) {
      const key = 'marketplace.previewWarnings.' + warning.code
      return this.$te(key) ? this.$t(key) : warning.message
    },
    onClose () {
      if (this.busy || this.importing) return
      this.$emit('close')
    },
    productMatches (product, q) {
      if (!product || !q) return false
      const haystacks = [
        product.id,
        product.name,
        product.description,
        product.price,
        product.image_url
      ]
      if (haystacks.some((v) => String(v == null ? '' : v).toLowerCase().indexOf(q) !== -1)) {
        return true
      }
      const options = Array.isArray(product.options) ? product.options : []
      for (let i = 0; i < options.length; i++) {
        const opt = options[i] || {}
        if (String(opt.id || '').toLowerCase().indexOf(q) !== -1) return true
        if (String(opt.name || '').toLowerCase().indexOf(q) !== -1) return true
        const values = Array.isArray(opt.values) ? opt.values : []
        for (let j = 0; j < values.length; j++) {
          const val = values[j] || {}
          if (String(val.id || '').toLowerCase().indexOf(q) !== -1) return true
          if (String(val.name || '').toLowerCase().indexOf(q) !== -1) return true
        }
      }
      try {
        return JSON.stringify(product).toLowerCase().indexOf(q) !== -1
      } catch (e) {
        return false
      }
    }
  }
}
</script>

<style scoped>
.menu-preview-modal {
  position: fixed;
  inset: 0;
  z-index: 10050;
  background: rgba(17, 24, 39, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.menu-preview-modal__dialog {
  width: 1100px;
  height: 780px;
  max-width: calc(100vw - 48px);
  max-height: calc(100vh - 48px);
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 50px rgba(0,0,0,.25);
}
.menu-preview-modal__header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  padding: 16px 18px;
  border-bottom: 1px solid #e5e7eb;
  flex: 0 0 auto;
}
.menu-preview-modal__heading {
  min-width: 0;
}
.menu-preview-modal__header h4 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
}
.menu-preview-modal__subtitle {
  margin: 4px 0 0;
  color: #6b7280;
  font-size: 13px;
}
.menu-preview-modal__actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}
.menu-preview-modal__close-btn {
  background: #ffebea !important;
  border: 1px solid #ff0d19 !important;
  color: #ff0d19 !important;
  border-radius: 50px !important;
  text-transform: none !important;
  font-weight: 600;
  padding: 7px 18px !important;
}
.menu-preview-modal__close-btn:hover,
.menu-preview-modal__close-btn:focus {
  background: #ffd4d2 !important;
  border-color: #ff0d19 !important;
  color: #ff0d19 !important;
  opacity: 1;
}
.menu-preview-modal__close-btn:disabled {
  opacity: 0.55;
}
.menu-preview-modal__toolbar {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 10px 18px;
  border-bottom: 1px solid #e5e7eb;
  flex: 0 0 auto;
  background: #fafafa;
}
.menu-preview-modal__search {
  flex: 1 1 auto;
  min-width: 0;
  height: 38px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 0 12px;
  font-size: 14px;
  outline: none;
  background: #fff;
}
.menu-preview-modal__search:focus {
  border-color: #20bfab;
  box-shadow: 0 0 0 3px rgba(32, 191, 171, 0.15);
}
.menu-preview-modal__search-clear {
  flex: 0 0 auto;
  border: 1px solid #d1d5db;
  background: #fff;
  border-radius: 10px;
  height: 38px;
  padding: 0 12px;
  cursor: pointer;
  color: #374151;
  font-size: 13px;
}
.menu-preview-modal__search-clear:hover {
  background: #f3f4f6;
}
.menu-preview-modal__content {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
}
.menu-preview-modal__importing {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.82);
  color: #6b7280;
  text-align: center;
  padding: 24px;
}
.menu-preview-modal__importing p {
  margin: 0;
  font-weight: 600;
}
.menu-preview-modal__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.menu-preview-modal__body > * {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
}
.menu-preview-modal__loading,
.menu-preview-modal__error {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #6b7280;
  text-align: center;
  padding: 24px;
}
.menu-preview-modal__loading p {
  margin: 0;
}
.menu-preview-modal__spinner {
  width: 42px;
  height: 42px;
  flex: 0 0 auto;
}
.menu-preview-modal__spinner-svg {
  width: 42px;
  height: 42px;
  display: block;
  animation: menu-preview-rotate 2s linear infinite;
}
.menu-preview-modal__spinner-path {
  stroke: #674bd2;
  stroke-width: 3;
  stroke-linecap: round;
  animation: menu-preview-dash 1.5s ease-in-out infinite;
}
@keyframes menu-preview-rotate {
  100% { transform: rotate(360deg); }
}
@keyframes menu-preview-dash {
  0% { stroke-dasharray: 1, 200; stroke-dashoffset: 0; }
  50% { stroke-dasharray: 90, 200; stroke-dashoffset: -35; }
  100% { stroke-dasharray: 90, 200; stroke-dashoffset: -125; }
}
.menu-preview-modal__error {
  color: #b91c1c;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Integrations/MenuPreview/IntegrationMenuPreviewModal.vue